import _toConsumableArray from "/usr/src/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import React from 'react';
import { observer } from 'mobx-react';
import { getSession, polarToCartesian, radToDeg, assembleLocString } from '@jbrowse/core/util';
import { makeContrasting } from '@jbrowse/core/util/color';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
var useStyles = makeStyles()({
  rulerLabel: {
    fontSize: '0.8rem',
    fontWeight: 500,
    lineHeight: 1.6,
    letterSpacing: '0.0075em'
  }
});
function sliceArcPath(slice, radiusPx, startBase, endBase) {
  // A rx ry x-axis-rotation large-arc-flag sweep-flag x y
  if (slice.flipped) {
    ;
    var _ref = [endBase, startBase];
    startBase = _ref[0];
    endBase = _ref[1];
  }
  var startXY = slice.bpToXY(startBase, radiusPx);
  var endXY = slice.bpToXY(endBase, radiusPx);
  var largeArc = Math.abs(endBase - startBase) / slice.bpPerRadian > Math.PI ? '1' : '0';
  var sweepFlag = '1';
  return ['M'].concat(_toConsumableArray(startXY), ['A', radiusPx, radiusPx, '0', largeArc, sweepFlag], _toConsumableArray(endXY)).join(' ');
}
var ElisionRulerArc = observer(function (_ref2) {
  var model = _ref2.model,
    slice = _ref2.slice,
    region = _ref2.region;
  var theme = useTheme();
  var modelRadiusPx = model.radiusPx;
  var radiusPx = modelRadiusPx + 1;
  var endRadians = slice.endRadians,
    startRadians = slice.startRadians;
  var startXY = polarToCartesian(radiusPx, startRadians);
  var endXY = polarToCartesian(radiusPx, endRadians);
  var widthPx = (endRadians - startRadians) * radiusPx;
  var largeArc = endRadians - startRadians > Math.PI ? '1' : '0';
  // TODO: draw the elision
  var centerRadians = (endRadians + startRadians) / 2;
  var regionCountString = "[".concat(Number(
  // @ts-ignore
  region.regions.length).toLocaleString(), "]");
  return React.createElement(React.Fragment, null, React.createElement(RulerLabel, {
    text: regionCountString,
    view: model,
    maxWidthPx: widthPx,
    radians: centerRadians,
    radiusPx: radiusPx,
    title: "".concat(Number(
    // @ts-ignore
    region.regions.length).toLocaleString(), " more regions"),
    color: theme.palette.text.primary
  }), React.createElement("path", {
    d: ['M'].concat(_toConsumableArray(startXY), ['A', radiusPx, radiusPx, '0', largeArc, '1'], _toConsumableArray(endXY)).join(' '),
    stroke: theme.palette.text.secondary,
    strokeWidth: 2,
    strokeDasharray: "2,2",
    fill: "none"
  }));
});
var RulerLabel = observer(function (_ref3) {
  var view = _ref3.view,
    text = _ref3.text,
    maxWidthPx = _ref3.maxWidthPx,
    radians = _ref3.radians,
    radiusPx = _ref3.radiusPx,
    title = _ref3.title,
    color = _ref3.color;
  var _useStyles = useStyles(),
    classes = _useStyles.classes;
  var textXY = polarToCartesian(radiusPx + 5, radians);
  if (!text) {
    return null;
  }
  if (text.length * 6.5 < maxWidthPx) {
    // text is rotated parallel to the ruler arc
    return React.createElement("text", {
      x: 0,
      y: 0,
      className: classes.rulerLabel,
      textAnchor: "middle",
      dominantBaseline: "baseline",
      transform: "translate(".concat(textXY, ") rotate(").concat(radToDeg(radians) + 90, ")"),
      style: {
        fill: color
      }
    }, text, React.createElement("title", null, title || text));
  }
  if (maxWidthPx > 4) {
    // text is rotated perpendicular to the ruler arc
    var overallRotation = radToDeg(radians + view.offsetRadians - Math.PI / 2);
    if (overallRotation >= 180) {
      return React.createElement("text", {
        x: 0,
        y: 0,
        className: classes.rulerLabel,
        textAnchor: "start",
        dominantBaseline: "middle",
        transform: "translate(".concat(textXY, ") rotate(").concat(radToDeg(radians), ")"),
        style: {
          fill: color
        }
      }, text, React.createElement("title", null, title || text));
    }
    return React.createElement("text", {
      x: 0,
      y: 0,
      className: classes.rulerLabel,
      textAnchor: "end",
      dominantBaseline: "middle",
      transform: "translate(".concat(textXY, ") rotate(").concat(radToDeg(radians) + 180, ")"),
      style: {
        fill: color
      }
    }, text, React.createElement("title", null, title || text));
  }
  // if you get here there is no room for the text at all
  return null;
});
var RegionRulerArc = observer(function (_ref4) {
  var model = _ref4.model,
    slice = _ref4.slice,
    region = _ref4.region;
  var theme = useTheme();
  var radiusPx = model.radiusPx;
  var endRadians = slice.endRadians,
    startRadians = slice.startRadians;
  var centerRadians = (endRadians + startRadians) / 2;
  var widthPx = (endRadians - startRadians) * radiusPx;
  var session = getSession(model);
  var color;
  var assembly = session.assemblyManager.get(region.assemblyName);
  if (assembly) {
    color = assembly.getRefNameColor(region.refName);
  }
  if (color) {
    try {
      color = makeContrasting(color, theme.palette.background.paper);
    } catch (error) {
      color = theme.palette.text.primary;
    }
  } else {
    color = theme.palette.text.primary;
  }
  // TODO: slice flipping
  return React.createElement(React.Fragment, null, React.createElement(RulerLabel, {
    text: region.refName,
    view: model,
    maxWidthPx: widthPx,
    radians: centerRadians,
    radiusPx: radiusPx,
    color: color
  }), React.createElement("path", {
    d: sliceArcPath(slice, radiusPx + 1, region.start, region.end),
    stroke: color,
    strokeWidth: 2,
    fill: "none"
  }));
});
export default observer(function (_ref5) {
  var model = _ref5.model,
    slice = _ref5.slice;
  if (slice.region.elided) {
    return React.createElement(ElisionRulerArc, {
      key: assembleLocString(slice.region.regions[0]),
      model: model,
      region: slice.region,
      slice: slice
    });
  }
  return React.createElement(RegionRulerArc, {
    key: assembleLocString(slice.region),
    region: slice.region,
    model: model,
    slice: slice
  });
});